import React from 'react';
import { Link } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import dayjs from 'dayjs';
import { ViewFile } from '@/components/ats/FileUpload/ViewFile';
import { CustomField } from '@/pages/requisitions/types';

interface CustomFieldRendererProps {
  value: any;
  fieldDefinition: CustomField | undefined;
  dateFormat: string;
}

export const CustomFieldRenderer: React.FC<CustomFieldRendererProps> = ({
  value,
  fieldDefinition,
  dateFormat,
}) => {
  if (value === null || value === undefined) {
    return <KText>-</KText>;
  }

  const fieldKind = fieldDefinition?.kind;

  // Date field (timestamp in milliseconds)
  if (fieldKind === 'date') {
    const dateValue =
      typeof value === 'number' ? new Date(value) : (value as string | Date);
    return <KText>{dayjs(dateValue).format(dateFormat)}</KText>;
  }

  // Boolean/Yes-No field
  if (fieldKind === 'enum_boolean') {
    const displayValue =
      value === true || value === 'true' || value === 'Y' ? 'Yes' : 'No';
    return <KText>{displayValue}</KText>;
  }

  // File field
  if (
    fieldKind === 'rel_file' &&
    typeof value === 'object' &&
    value !== null &&
    ('url' in value || 'filename' in value)
  ) {
    return <ViewFile file={value} showDelete={false} />;
  }

  // Select field (rel_tag) - handles both single and multi
  if (fieldKind === 'rel_tag') {
    return renderSelectField(value, fieldDefinition);
  }

  // URL field - make it clickable
  if (fieldKind === 'url' && typeof value === 'string') {
    return (
      <Link
        href={value}
        color="primary-600"
        fontSize="sm"
        isExternal
        _hover={{ textDecoration: 'underline' }}
      >
        {value}
      </Link>
    );
  }

  // Text/Long text field - preserve line breaks
  if (
    (fieldKind === 'text' || fieldKind === 'string') &&
    typeof value === 'string'
  ) {
    return (
      <KText whiteSpace="pre-wrap" wordBreak="break-word">
        {String(value)}
      </KText>
    );
  }

  // Number field
  if (fieldKind === 'number') {
    return <KText>{String(value)}</KText>;
  }

  // Array values (fallback)
  if (Array.isArray(value)) {
    return <KText>{value.map(String).join(', ')}</KText>;
  }

  // Object values (fallback)
  if (typeof value === 'object') {
    return (
      <KText whiteSpace="pre-wrap" wordBreak="break-word">
        {JSON.stringify(value)}
      </KText>
    );
  }

  // Default: string/number
  return (
    <KText whiteSpace="pre-wrap" wordBreak="break-word">
      {String(value)}
    </KText>
  );
};

// Helper function for select field rendering
const renderSelectField = (
  value: any,
  fieldDefinition: CustomField | undefined
) => {
  // Multi-select (array of IDs)
  if (Array.isArray(value)) {
    if (fieldDefinition?.ats_tag_kind?.ats_tags) {
      const labels = value
        .map((id) => {
          // If it's an object with value property
          if (typeof id === 'object' && id !== null && 'value' in id) {
            return (id as any).value;
          }
          // If it's an ID, look it up
          if (typeof id === 'number') {
            const tag = fieldDefinition.ats_tag_kind.ats_tags.find(
              (t) => t.id === id
            );
            return tag?.value;
          }
          return null;
        })
        .filter(Boolean)
        .join(', ');
      return <KText>{labels || '-'}</KText>;
    }
    return <KText>{value.map(String).join(', ')}</KText>;
  }

  // Single select - ID lookup
  if (typeof value === 'number' && fieldDefinition?.ats_tag_kind?.ats_tags) {
    const tag = fieldDefinition.ats_tag_kind.ats_tags.find(
      (t) => t.id === value
    );
    return <KText>{tag?.value || String(value)}</KText>;
  }

  // Single select (tag object with value property)
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return <KText>{String((value as any).value)}</KText>;
  }

  return <KText>{String(value)}</KText>;
};
